/**
 * Census Bureau API client for demographic data overlays.
 *
 * Two external APIs, both free and CORS-enabled:
 * - TIGERweb: Block group boundary polygons (returns GeoJSON directly)
 * - ACS 5-Year: Demographic statistics per block group
 *
 * Optional: Set VITE_CENSUS_API_KEY for higher rate limits (free key from census.gov).
 * Without a key, you get 500 queries/day which is plenty for development.
 */

const TIGERWEB_BASE =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Tracts_Blocks/MapServer';
const CENSUS_ACS_BASE = 'https://api.census.gov/data/2023/acs/acs5';
const BLOCK_GROUP_LAYER = 1; // "Census Block Groups" — current vintage

// ACS variable codes
const ACS_VARS = [
  'B19013_001E', // Median household income
  'B25003_001E', // Housing tenure: total occupied
  'B25003_003E', // Housing tenure: renter-occupied
  'B15003_001E', // Education: total population 25+
  'B15003_022E', // Education: bachelor's degree
  'B15003_023E', // Education: master's degree
  'B15003_024E', // Education: professional degree
  'B15003_025E', // Education: doctorate
  'B03002_001E', // Race: total population
  'B03002_003E', // Race: white alone (non-Hispanic)
  'B03002_004E', // Race: Black alone (non-Hispanic)
  'B03002_012E', // Race: Hispanic/Latino (any race)
] as const;

export interface BlockGroupDemographics {
  geoid: string;
  medianIncome: number | null;
  renterRate: number;
  collegeRate: number;
  pctWhite: number;
  pctBlack: number;
  pctHispanic: number;
}

/**
 * Fetch block group boundary polygons from TIGERweb for a bounding box.
 * Returns GeoJSON with GEOID, STATE, COUNTY properties on each feature.
 */
export async function fetchBlockGroupBoundaries(
  bounds: [west: number, south: number, east: number, north: number],
): Promise<GeoJSON.FeatureCollection> {
  const [west, south, east, north] = bounds;
  const url = new URL(`${TIGERWEB_BASE}/${BLOCK_GROUP_LAYER}/query`);
  url.searchParams.set('geometry', `${west},${south},${east},${north}`);
  url.searchParams.set('geometryType', 'esriGeometryEnvelope');
  url.searchParams.set('spatialRel', 'esriSpatialRelIntersects');
  url.searchParams.set('outFields', 'GEOID,STATE,COUNTY,TRACT,BLKGRP,BASENAME');
  url.searchParams.set('f', 'geojson');
  url.searchParams.set('inSR', '4326');
  url.searchParams.set('outSR', '4326');
  url.searchParams.set('resultRecordCount', '5000');

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`TIGERweb request failed: ${res.status}`);
  return res.json();
}

/**
 * Fetch ACS 5-Year demographics for all block groups in a county.
 * Returns a Map keyed by 12-digit GEOID (state+county+tract+blockgroup).
 *
 * Census sentinel values (negative numbers like -666666666) indicate
 * missing/suppressed data and are converted to null.
 */
export async function fetchCountyDemographics(
  stateFips: string,
  countyFips: string,
  apiKey?: string,
): Promise<Map<string, BlockGroupDemographics>> {
  const params = new URLSearchParams({
    get: ACS_VARS.join(','),
    for: 'block group:*',
    in: `state:${stateFips} county:${countyFips}`,
  });
  if (apiKey) params.set('key', apiKey);

  const res = await fetch(`${CENSUS_ACS_BASE}?${params}`);
  if (!res.ok) {
    throw new Error(`Census API failed: ${res.status} for state=${stateFips} county=${countyFips}`);
  }

  const rows: string[][] = await res.json();
  const [headers, ...data] = rows;
  const col = (name: string) => headers.indexOf(name);

  const result = new Map<string, BlockGroupDemographics>();

  for (const row of data) {
    const geoid = `${row[col('state')]}${row[col('county')]}${row[col('tract')]}${row[col('block group')]}`;

    const rawIncome = Number(row[col('B19013_001E')]);
    const totalPop = Number(row[col('B03002_001E')]) || 0;
    const totalHousing = Number(row[col('B25003_001E')]) || 0;
    const totalEd = Number(row[col('B15003_001E')]) || 0;
    const collegeEd =
      (Number(row[col('B15003_022E')]) || 0) +
      (Number(row[col('B15003_023E')]) || 0) +
      (Number(row[col('B15003_024E')]) || 0) +
      (Number(row[col('B15003_025E')]) || 0);

    result.set(geoid, {
      geoid,
      medianIncome: rawIncome > 0 ? rawIncome : null,
      renterRate: totalHousing > 0 ? (Number(row[col('B25003_003E')]) || 0) / totalHousing : 0,
      collegeRate: totalEd > 0 ? collegeEd / totalEd : 0,
      pctWhite: totalPop > 0 ? (Number(row[col('B03002_003E')]) || 0) / totalPop : 0,
      pctBlack: totalPop > 0 ? (Number(row[col('B03002_004E')]) || 0) / totalPop : 0,
      pctHispanic: totalPop > 0 ? (Number(row[col('B03002_012E')]) || 0) / totalPop : 0,
    });
  }

  return result;
}
